import React, { useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { store } from '../../../../store'

// Components
import { ProgressList } from '../styles'

// Keyboard Shortcuts
import { connectShortcuts, handleOrderPermissions } from '../../../../keyboard-shortcuts'

// Helpers
import { notify } from '../../../../components/component-items/helpers'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBoxAlt,
  faBarcodeRead,
  faRulerCombined,
  faPrint,
  faShippingFast,
  faChevronRight,
  faBoxCheck,
  faTruckContainer,
} from '@fortawesome/pro-duotone-svg-icons'

import { MenuOptionProps, ShippingWorkflowProps } from './types'
import { isNonParcelMode, isNonParcel } from '../../../../types/transport'

const MenuOption = ({
  url,
  onClick,
  icon,
  title,
  arrow,
  children,
  node,
  active,
  enabled,
  complete,
  id,
}: MenuOptionProps) => {
  const handleClick = (e: React.MouseEvent) => {
    if (!enabled) {
      e.preventDefault()
      e.stopPropagation()
      notify({ type: 'warning', message: 'Cannot perform action. Complete previous steps first.' })
      return
    }
    if (onClick) {
      onClick()
    }
  }

  return (
    <li
      id={id}
      ref={node}
      onClick={handleClick}
      className={active ? 'active' : !enabled ? 'disabled' : complete ? 'complete' : ''}
    >
      <Link to={url && enabled ? url : '#'}>
        <span className="icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="text">{title}</span>
        {arrow ? <FontAwesomeIcon icon={faChevronRight} style={{ color: '#595d6e' }} /> : null}
      </Link>
      {children}
    </li>
  )
}

export const ShippingWorkflow = ({
  current,
  nextOrder,
  fully_packed,
  needs_serial_numbers,
  serial_number_enabled,
  needs_dimensions,
  labeled,
  pending,
  transport_mode,
  contracting_party,
  onBeforeNavigate,
}: ShippingWorkflowProps) => {
  const globalState = useContext(store)
  const {
    state: { userIsWarehouse },
  } = globalState

  const navigate = useNavigate()
  const order = useParams<{ id: string }>().id || ''
  const url = `/order/${order}`

  // Derive freight status from transport_mode
  const isFreight = isNonParcelMode(transport_mode)
  const isParcel = !isNonParcel(transport_mode)
  // Non-parcel orders (freight or will_call) skip the label purchase step
  // Non-shipper contracting party on parcel orders also skips EasyPost
  const hasCustomerShipping = Boolean(isParcel && contracting_party && contracting_party !== 'shipper')
  const skipLabelPurchase = isNonParcel(transport_mode) || hasCustomerShipping

  // Keyboard Shortcuts:
  // Attaches listener on order page for keyboard shortcuts
  // such as: Reset Packing, Add Package, Confirm Dimensions, etc
  useEffect(() => {
    if (userIsWarehouse) {
      const handleKeyDown = (event: any) => {
        handleOrderPermissions(order, fully_packed, needs_dimensions, labeled, navigate)
        connectShortcuts(event, order, 'order', navigate)
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fully_packed, needs_dimensions, labeled])

  return (
    <ProgressList>
      <ul>
        <MenuOption url={url + '/pack/'} active={current === 'pack'} enabled icon={faBoxAlt} title={'Pack Order'} />
        {serial_number_enabled ? (
          <MenuOption
            url={url + '/serial-numbers/'}
            active={current === 'serial_numbers'}
            icon={faBarcodeRead}
            enabled={fully_packed}
            title={'Add Serial Number'}
          />
        ) : null}
        <MenuOption
          url={url + '/confirm-dimensions/'}
          active={current === 'confirm_dimensions'}
          icon={faRulerCombined}
          enabled={fully_packed && !needs_serial_numbers}
          title={'Confirm Dimensions'}
        />

        {!skipLabelPurchase && (
          <MenuOption
            url={url + '/label-purchase/'}
            active={current === 'label_purchase'}
            icon={faPrint}
            enabled={fully_packed && !needs_serial_numbers && !needs_dimensions}
            title={'Purchase Label'}
          />
        )}

        {(isFreight || hasCustomerShipping) && (
          <MenuOption
            url={url + '/shipment-details/'}
            active={current === 'shipment-details'}
            icon={faTruckContainer}
            enabled={fully_packed && !needs_serial_numbers && !needs_dimensions}
            title={'Shipment Details'}
          />
        )}

        {nextOrder ? (
          <MenuOption
            id="ship-next-order"
            onClick={async () => {
              await onBeforeNavigate?.()
              navigate(`/order/${nextOrder}/pack/`)
            }}
            url={`/order/${nextOrder}/pack/`}
            icon={faShippingFast}
            enabled={fully_packed && !needs_dimensions && (skipLabelPurchase || labeled || pending)}
            title={'Ship Next Order'}
          />
        ) : (
          <MenuOption
            id="ship-next-order"
            onClick={async () => {
              await onBeforeNavigate?.()
              navigate('/batch/')
            }}
            url={'/batch/'}
            icon={faBoxCheck}
            enabled={fully_packed && !needs_dimensions && (skipLabelPurchase || labeled || pending)}
            title={'Complete'}
          />
        )}
      </ul>
    </ProgressList>
  )
}
